import React, { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { LogsService } from '@/shared/api'
import { PageHeader, Button, Card } from '@/shared/ui'

export const LogsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [logFiles, setLogFiles] = useState<any[]>([])
  const [isDownloading, setIsDownloading] = useState<Record<string, boolean>>({})

  useEffect(() => {
    loadLogsList()
  }, [])

  const loadLogsList = async () => {
    try {
      setIsLoading(true)
      const response = await LogsService.getLogsList()
      setLogFiles(response.logs || [])
    } catch (error) {
      console.error('Error loading logs list:', error)
      toast.error('Ошибка загрузки логов')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownload = async (filename: string) => {
    try {
      setIsDownloading(prev => ({ ...prev, [filename]: true }))
      await LogsService.downloadLog(filename)
      toast.success('Файл успешно скачан')
    } catch (error: any) {
      console.error('Error downloading log:', error)
      toast.error('Ошибка скачивания файла')
    } finally {
      setIsDownloading(prev => ({ ...prev, [filename]: false }))
    }
  }
  
  const getLogTitle = (filename: string) => {
    if (filename.includes('webhooks')) {
      return 'Логи вебхуков'
    }
    return 'Логи API запросов'
  }
  
  const getLogDescription = (filename: string) => {
    if (filename.includes('webhooks')) {
      return 'Входящие и исходящие вебхуки'
    }
    return 'Все запросы к API'
  }

  const formatSize = (sizeMb: number) => {
    if (sizeMb < 1) {
      return `${(sizeMb * 1024).toFixed(2)} KB`
    }
    return `${sizeMb.toFixed(2)} MB`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Загрузка логов...</div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Логи системы"
        description="Скачайте файлы с логами API запросов и вебхуков"
      />

      {/* Список всех файлов логов */}
      {logFiles.length > 0 ? (
        <div className="space-y-4">
          {logFiles.map((logFile) => (
            <Card key={logFile.name}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${
                    logFile.name.includes('webhooks') ? 'bg-purple-100' : 'bg-blue-100'
                  }`}>
                    <DocumentTextIcon className={`h-8 w-8 ${
                      logFile.name.includes('webhooks') ? 'text-purple-600' : 'text-blue-600'
                    }`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {getLogTitle(logFile.name)}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">{logFile.name}</p>
                    <p className="text-xs text-gray-400 mt-1">{getLogDescription(logFile.name)}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span className="font-medium">{formatSize(logFile.size_mb || 0)}</span>
                      <span>•</span>
                      <span className="text-xs">Обновлён: {logFile.modified_at_formatted || '—'}</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => handleDownload(logFile.name)}
                  disabled={!!isDownloading[logFile.name]}
                >
                  <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                  {isDownloading[logFile.name] ? 'Скачивание...' : 'Скачать'}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="px-6 py-12 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Файлы логов отсутствуют</h3>
            <p className="mt-1 text-sm text-gray-500">
              Логи появятся после первых запросов к API или вебхукам
            </p>
          </div>
        </Card>
      )}
    </div>
  )
}


